// DoctorRatingsForm.tsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import AlertBox from "../../components/common/AlertBox";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormRatingField from "../../components/formFields/FormRatingField";
import ExpandableText from "../../components/common/ExpandableText";
import IconComponent from "../../components/common/IconComponent";
import { MdDelete, MdEdit, MdStar, MdStarBorder } from "react-icons/md";
import {
  deleteDoctorRating,
  getDoctorRatingsByDoctorID,
  insertDoctorRating,
  updateDoctorRating,
} from "./services/doctor.service";
import { clearDoctorRatings } from "./doctorSlice";

interface PageState {
  skip: number;
  take: number;
}
const initialDataState: PageState = { skip: 0, take: 10 };

const DoctorRatingsForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const DoctorID = location.state?.DoctorID;

  const [editRating, setEditRating] = useState<any>(null);
  const [deleteRatingId, setDeleteRatingId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(1);
  const [page, setPage] = useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const ratings = useAppSelector((state) => state.doctor.DoctorRatings);
  const loading = useAppSelector((state) => state.doctor.loading);
  const formLoading = useAppSelector((state) => state.doctor.formLoading);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [ratings, editRating]);

  useEffect(() => {
    if (DoctorID) dispatch(getDoctorRatingsByDoctorID(DoctorID));
    return () => {
      dispatch(clearDoctorRatings());
    };
  }, [DoctorID]);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? ratings.length : event.page.take;
    if (targetEvent.value) setPageSizeValue(targetEvent.value);
    setPage({ ...event.page, take });
  };

  const handleAddRating = async (values: any) => {
    const response = await dispatch(
      insertDoctorRating({
        DoctorID,
        Rating: parseInt(values.Rating),
        PatientName: values.PatientName,
        Title: values.Title,
        Reviews: values.Reviews,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorRatingsByDoctorID(DoctorID));
      setEditRating(null);
    }
  };

  const handleUpdateRating = async (values: any) => {
    const response = await dispatch(
      updateDoctorRating({
        ID: editRating.ID,
        DoctorID,
        Rating: parseInt(values.Rating),
        PatientName: values.PatientName,
        Title: values.Title,
        Reviews: values.Reviews,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getDoctorRatingsByDoctorID(DoctorID));
      setEditRating(null);
    }
  };

  const handleDeleteRating = async () => {
    if (deleteRatingId) {
      const response = await dispatch(deleteDoctorRating(deleteRatingId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getDoctorRatingsByDoctorID(DoctorID));
        dispatch(closeDialog());
        setDeleteRatingId(null);
      }
    }
  };

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        {[...Array(full)].map((_, i) => (
          <IconComponent
            icon={MdStar}
            key={`f-${i}`}
            style={{ color: "#ffc107", fontSize: "16px" }}
          />
        ))}
        {[...Array(5 - full)].map((_, i) => (
          <IconComponent
            icon={MdStarBorder}
            key={`e-${i}`}
            style={{ color: "#ffc107", fontSize: "16px" }}
          />
        ))}
      </div>
    );
  };

  const MyEditCommandCell = (props: any) => (
    <td
      className={props.className}
      style={{
        ...props.style,
        textAlign: "center",
        display: "flex",
        justifyContent: "space-evenly",
      }}
    >
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Edit"
        onClick={() => setEditRating({ ...props.dataItem })}
        icon={MdEdit}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "20px" }}
      />
      <IconButton
        type="button"
        fillMode="flat"
        size="small"
        title="Delete"
        className="color-red"
        onClick={() => {
          dispatch(openDialog("deleteDoctorRating"));
          setDeleteRatingId(props.dataItem.ID);
        }}
        icon={MdDelete}
        iconClassName="absolute-position"
        iconStyle={{ fontSize: "24px" }}
      />
    </td>
  );

  const RatingCell = (props: any) => (
    <td style={{ ...props.style }} className={props.className}>
      <div className="d-flex">
        {renderStars(props.dataItem?.Rating)}
        <span style={{ marginLeft: "8px", fontSize: "12px", color: "#666" }}>
          ({props.dataItem?.Rating}/5)
        </span>
      </div>
    </td>
  );

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 3 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...(props as any).tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  return (
    <>
      {dialogName === "deleteDoctorRating" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Rating & Review</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this rating?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{ display: "flex", justifyContent: "end", gap: "10px" }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDeleteRating}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              {editRating ? "Edit Rating & Review" : "Add New Rating & Review"}
            </Typography.h4>
            <Form
              key={formKey}
              onSubmit={editRating ? handleUpdateRating : handleAddRating}
              initialValues={
                editRating || { Rating: "", Title: "", Reviews: "" }
              }
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout gap={{ rows: 15, cols: 20 }}>
                    <GridLayoutItem colSpan={2} rowSpan={2}>
                      <div style={{ display: "flex", gap: "20px" }}>
                        <Field
                          wrapperClassName="w-100"
                          name="PatientName"
                          label="Patient Name"
                          component={FormTextField}
                          validator={requiredValidator}
                          astrike={true}
                        />
                        <Field
                          wrapperClassName="w-100"
                          name="Rating"
                          label="Rating (1-5)"
                          component={FormRatingField}
                          type="number"
                          validator={requiredValidator}
                          astrike={true}
                        />
                      </div>
                      <Field
                        name="Title"
                        label="Review Title"
                        placeholder="e.g., Excellent Doctor"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="Reviews"
                        label="Review Description"
                        placeholder="Write your detailed review..."
                        component={FormTextArea}
                        rows={4}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        gap: "10px",
                      }}
                    >
                      {editRating && (
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"secondary"}
                          onClick={() => setEditRating(null)}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <ButtonWithLoading
                        label={editRating ? "Update Review" : "Add Review"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />

            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              All Reviews ({ratings?.length || 0})
            </Typography.h4>
            {ratings && ratings.length > 0 ? (
              <KendoGrid
                style={{ maxHeight: "calc(100vh - 400px)" }}
                data={ratings.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={ratings.length}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, 15, "All"],
                  pageSizeValue,
                  type: "input",
                }}
                onPageChange={pageChange}
                cells={{ headerCell: HeaderCustomCell, data: MyDataCustomCell }}
              >
                <Column field="PatientName" title="Patient Name" width={200} />
                <Column field="Title" title="Review Title" width={200} />
                <Column
                  field="Rating"
                  title="Rating"
                  width={150}
                  cells={{ data: RatingCell }}
                  filterable={false}
                />
                <Column
                  field="Reviews"
                  title="Review"
                  cells={{
                    data: (props: any) => (
                      <td
                        style={{ ...props.style }}
                        className={props.className}
                      >
                        <ExpandableText text={props.dataItem?.Reviews} />
                      </td>
                    ),
                  }}
                />
                <Column
                  field="ID"
                  title="Actions"
                  width={110}
                  cells={{ data: MyEditCommandCell }}
                  filterable={false}
                  locked={true}
                />
              </KendoGrid>
            ) : (
              <AlertBox />
            )}
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default DoctorRatingsForm;
